// src/components/providerAssignments/assignmentDisplay.ts
//
// Display constants + helpers for Provider Assignment enums. Kept as
// plain data so both dialogs and the wizard read the same source.

import type {
  AssignmentPriority,
  AssignmentStatus,
  AssignmentHistoryAction,
} from '../../types/api';

export const PRIORITY_LABELS: Record<AssignmentPriority, string> = {
  primary: 'Primary',
  secondary: 'Secondary',
  backup: 'Backup',
  escalation: 'Escalation',
  consultant: 'Consultant',
};

export const PRIORITY_DESCRIPTIONS: Record<AssignmentPriority, string> = {
  primary: 'Lead provider for this person in this department (at most one).',
  secondary: 'Additional provider supporting the primary.',
  backup: 'Fallback provider when the primary is unavailable.',
  escalation: 'Escalation contact.',
  consultant: 'Subject-matter consultant.',
};

/** Tailwind classes for a compact priority chip. Includes text + bg + ring. */
export const PRIORITY_CHIP: Record<AssignmentPriority, string> = {
  primary: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  secondary: 'bg-sky-50 text-sky-700 ring-sky-200',
  backup: 'bg-slate-50 text-slate-700 ring-slate-200',
  escalation: 'bg-amber-50 text-amber-700 ring-amber-200',
  consultant: 'bg-purple-50 text-purple-700 ring-purple-200',
};

export const STATUS_LABELS: Record<AssignmentStatus, string> = {
  proposed: 'Proposed',
  active: 'Active',
  temporarily_inactive: 'Temporarily inactive',
  on_leave: 'On leave',
  reassigned: 'Reassigned',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const STATUS_CHIP: Record<AssignmentStatus, string> = {
  proposed: 'bg-slate-50 text-slate-600 ring-slate-200',
  active: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  temporarily_inactive: 'bg-amber-50 text-amber-700 ring-amber-200',
  on_leave: 'bg-amber-50 text-amber-700 ring-amber-200',
  reassigned: 'bg-slate-100 text-slate-600 ring-slate-300',
  completed: 'bg-slate-100 text-slate-600 ring-slate-300',
  cancelled: 'bg-red-50 text-red-700 ring-red-200',
};

export const HISTORY_ACTION_LABELS: Record<AssignmentHistoryAction, string> = {
  created: 'Assignment created',
  provider_changed: 'Provider changed',
  role_changed: 'Role changed',
  priority_changed: 'Priority changed',
  status_changed: 'Status changed',
  dates_changed: 'Dates updated',
  reassigned: 'Reassigned to new provider',
  ended: 'Assignment ended',
  restored: 'Assignment restored',
};

export const ACTIVE_STATUSES: readonly AssignmentStatus[] = [
  'proposed',
  'active',
  'temporarily_inactive',
  'on_leave',
];

export function isTerminalStatus(s: AssignmentStatus): boolean {
  return s === 'reassigned' || s === 'completed' || s === 'cancelled';
}

/**
 * Given a full assignment row, return the user-facing display name for
 * the provider. Falls back to email → id so we never render "null".
 */
export function providerLabel(
  provider: { fullName?: string | null; displayName?: string | null; email?: string } | null | undefined,
): string {
  if (!provider) return 'Unknown';
  return provider.fullName || provider.displayName || provider.email || 'Unknown';
}

export function contactLabel(
  contact: { fullName?: string | null; email?: string | null; phone?: string | null } | null | undefined,
): string {
  if (!contact) return 'Unknown';
  return contact.fullName || contact.email || contact.phone || 'Unknown';
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
}
